import { Platform } from "react-native";
import Constants from "expo-constants";
import * as Sentry from "@sentry/react-native";
import { supabase } from "@lezz/supabase";

declare const __DEV__: boolean;

function getApiBaseUrl(): string {
  // Web: relative URLs work (same origin)
  if (Platform.OS === "web") return "";

  // Native dev: derive from metro bundler host
  if (__DEV__) {
    const hostUri = Constants.expoConfig?.hostUri;
    if (hostUri) {
      return `http://${hostUri}`;
    }
  }

  // Native prod: requires explicit API URL
  const apiUrl = process.env.EXPO_PUBLIC_DOMAIN_URL;
  if (!apiUrl) {
    throw new Error(
      "EXPO_PUBLIC_DOMAIN_URL is required for production native builds"
    );
  }
  return apiUrl;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiFetchOptions extends Omit<RequestInit, "headers"> {
  token?: string;
  headers?: Record<string, string>;
}

export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {}
): Promise<T> {
  const { token: explicitToken, headers, ...rest } = options;
  const API_BASE = getApiBaseUrl();

  const token =
    explicitToken ??
    (await supabase.auth.getSession()).data.session?.access_token;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10_000);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...rest,
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
    });
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const method = options.method ?? "GET";
    const errorMessage = body?.error ?? `HTTP ${response.status}`;
    const details = body?.details ?? null;
    const error = new ApiError(errorMessage, response.status);

    const isServerError = response.status >= 500;
    const isValidationMismatch = response.status === 400 && details !== null;
    if (isServerError || isValidationMismatch) {
      Sentry.captureException(error, {
        tags: {
          "api.path": path,
          "api.method": method,
          "api.status": response.status,
        },
        contexts: {
          api: {
            path,
            method,
            status: response.status,
            errorMessage,
            ...(details ? { validationDetails: details } : {}),
          },
        },
      });
    }

    throw error;
  }

  return response.json();
}
